// Same-origin JSON client for docs/API.md. Writes send X-CSRF-Token = the readable
// __Host-arena_csrf cookie set at login (double-submit; API.md §1.2).
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, body: ApiErrorBody["error"]) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

const CSRF_COOKIES = ["__Host-arena_csrf", "arena_csrf"]; // second name: http://localhost dev

export function csrfToken(): string | null {
  for (const part of document.cookie.split("; ")) {
    const eq = part.indexOf("=");
    if (eq > 0 && CSRF_COOKIES.includes(part.slice(0, eq))) return decodeURIComponent(part.slice(eq + 1));
  }
  return null;
}

const SAFE_NEXT = /^\/(?![/\\])[^\s\\]*$/; // arena.auth.service.safe_next
/** Pages that must not be a post-login target (they would loop back into sign-in). */
const NO_RETURN = /^\/(login|oauth|auth)(\/|\?|$)/;

/** Safe in-site `next`: the server's rule (API.md §2.1) plus no sign-in pages; anything else -> /hub. */
export function safeNext(next: string | null | undefined): string {
  return next && next.length <= 512 && SAFE_NEXT.test(next) && !NO_RETURN.test(next) ? next : "/hub";
}

/** Fired when an API call answers 401: the session ended, SessionProvider re-checks /api/me. */
export const UNAUTHENTICATED_EVENT = "arena:unauthenticated";

/** Full-page OAuth round trip; used for sign-in and for the lab's step-up actions (API.md §1.2, §2.1). */
export function loginUrl(next: string = window.location.pathname + window.location.search): string {
  return `/auth/login?next=${encodeURIComponent(next)}`;
}

export interface ApiInit extends Omit<RequestInit, "body"> {
  json?: unknown;
}

export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set("Accept", "application/json");
  headers.set("Accept-Language", document.documentElement.lang || "en");
  let body: string | undefined;
  if (json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(json);
  }
  const method = (rest.method ?? "GET").toUpperCase();
  const csrf = csrfToken();
  if (!["GET", "HEAD"].includes(method) && csrf) headers.set("X-CSRF-Token", csrf);
  const url = new URL(path, window.location.origin);
  const res = await fetch(url, { ...rest, method, headers, body, credentials: "same-origin" });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new ApiError(res.status, data?.error ?? { code: `http_${res.status}`, message: res.statusText });
    if (res.status === 401 && url.pathname !== "/api/me") window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    if (err.code === "step_up_required") window.location.assign(loginUrl());
    throw err;
  }
  return data as T;
}

export const post = <T>(path: string, json?: unknown, init: ApiInit = {}) => api<T>(path, { ...init, method: "POST", json: json ?? {} });
export const put = <T>(path: string, json: unknown) => api<T>(path, { method: "PUT", json });

/** ULID-ish random key accepted by vote_envelope idempotency_key (^[A-Za-z0-9_-]{16,64}$). */
export function idempotencyKey(): string {
  return crypto.randomUUID();
}
